import React, { FC, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Stack, TextFieldProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { SDateLabel, STextField } from './styles';
import SvgIconStyle from '../SvgIconStyle';

type Props = {
  leftName: string;
  rightName: string;
  minDate?: Date;
};

const parseDateValue = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const RHFGroupDateField: FC<Props & TextFieldProps> = ({
  leftName,
  rightName,
  minDate,
  ...other
}) => {
  const { control, setValue } = useFormContext();
  const [openFrom, setOpenFrom] = useState(false);
  const handleOpenFrom = () => {
    setOpenFrom(true);
  };
  const handleCloseFrom = () => {
    setOpenFrom(false);
  };

  const [openTo, setOpenTo] = useState(false);
  const handleOpenTo = () => {
    setOpenTo(true);
  };
  const handleCloseTo = () => {
    setOpenTo(false);
  };

  return (
    <Stack direction="row" maxWidth="500px">
      <SDateLabel start>
        <SvgIconStyle
          src="/icons/iconly/light/calendar.svg"
          sx={{ position: 'absolute', left: 5, width: '30px' }}
        />
      </SDateLabel>
      <Controller
        name={leftName}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <DatePicker
            open={openFrom}
            onOpen={handleOpenFrom}
            onClose={handleCloseFrom}
            value={parseDateValue(field.value)}
            onChange={(newValue: Date | null) =>
              setValue(field.name, newValue?.toLocaleDateString('en-US') || '', {
                shouldDirty: true,
              })
            }
            minDate={minDate}
            slots={{
              openPickerButton: () => null,
              textField: (params) => (
                <STextField
                  {...params}
                  startHint={true}
                  endHint={false}
                  firstInput={true}
                  {...other}
                  {...field}
                  value={field.value ?? ''}
                  error={!!error}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: null,
                    readOnly: false,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: false,
                  }}
                  onClick={handleOpenFrom}
                  helperText={error?.message}
                  placeholder="from"
                  size="small"
                />
              ),
            }}
          />
        )}
      />
      <Controller
        name={rightName}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <DatePicker
            open={openTo}
            onOpen={handleOpenTo}
            onClose={handleCloseTo}
            value={parseDateValue(field.value)}
            onChange={(newValue: Date | null) =>
              setValue(field.name, newValue?.toLocaleDateString('en-US') || '', {
                shouldDirty: true,
              })
            }
            minDate={minDate}
            slots={{
              openPickerButton: () => null,
              textField: (params) => (
                <STextField
                  {...params}
                  startHint={true}
                  endHint={false}
                  firstInput={false}
                  {...other}
                  {...field}
                  value={field.value ?? ''}
                  error={!!error}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: null,
                    readOnly: false,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: false,
                  }}
                  onClick={handleOpenTo}
                  helperText={error?.message}
                  placeholder="to"
                  size="small"
                />
              ),
            }}
          />
        )}
      />
    </Stack>
  );
};

export default RHFGroupDateField;
